import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import {
  selectUserData,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { upsertFetcherObjects } from "../../../../features/fetcherSettingsSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  FETCHER_CAPABILITY,
  FETCHER_VIEW_PERMISSION,
} from "../helpers/fetcherPermissions";
import { rowId, selectDevObjectRows } from "../helpers/fetcherCatalogue";
import { LEVEL_ORDER } from "../helpers/fetcherSettingsResolver";
import { nowStamp } from "../helpers/formatters";
import { nextLocalId } from "../helpers/localIds";
import { SortDirection } from "../helpers/tableSorting";
import {
  readPageParam,
  readRowsPerPageParam,
  readSearchParam,
  writePageParam,
  writeRowsPerPageParam,
  writeSearchParam,
} from "../helpers/urlParams";
import DeleteDevObjectModal from "./DeleteDevObjectModal";
import DevObjectForm, { DevObjectDraft } from "./DevObjectForm";
import DevObjectsFilterCard from "./DevObjectsFilterCard";
import DevObjectsSkeleton from "./DevObjectsSkeleton";
import DevObjectsTable, { DevObjectTableRow } from "./DevObjectsTable";
import ImportDevObjectsCsvModal from "./ImportDevObjectsCsvModal";

const DEFAULT_SORT_KEY = "id";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

/** The one object_type that is legitimately outside the four levels. */
const SYSTEM_DEFAULT = "system-default";

const NEW_DRAFT: DevObjectDraft = {
  raw: "system-default",
  mid: "1",
  k: "",
  v: "",
};

const DevObjects = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const canEdit = CheckPermissions(
    userData?.data?.products?.data_collection,
    FETCHER_CAPABILITY,
    FETCHER_VIEW_PERMISSION,
  );

  const { fetcherSettingsDevObjsData, objects, isCatalogueLoading } =
    useSelector((state: AppState) => state.fetcherSettings);

  const selectedFieldType = readSearchParam(searchParams.get("search"));
  const page = readPageParam(searchParams.get("page"));
  const rowsPerPage = readRowsPerPageParam(searchParams.get("page_size"));
  const [selectedObjectTypes, setSelectedObjectTypes] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION,
  );
  const [draft, setDraft] = useState<DevObjectDraft | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const devObjects = useMemo(
    () => selectDevObjectRows(fetcherSettingsDevObjsData),
    [fetcherSettingsDevObjsData],
  );

  const objectTypeOptions = useMemo(() => {
    const counts: { [key: string]: number } = {};
    devObjects.forEach((row) => {
      const objectType = row.object_type ?? "";
      counts[objectType] = (counts[objectType] ?? 0) + 1;
    });
    return Object.keys(counts)
      .sort()
      .map((objectType) => ({
        value: objectType,
        label: `${objectType} (${counts[objectType]})`,
      }));
  }, [devObjects]);

  const searchOptions = useMemo(
    () =>
      [
        ...new Set(
          devObjects
            .map((row) => row.field_type)
            .filter((fieldType): fieldType is string => !!fieldType),
        ),
      ].sort(),
    [devObjects],
  );

  const rows: DevObjectTableRow[] = useMemo(
    () =>
      devObjects
        .filter(
          (row) =>
            (!selectedFieldType || row.field_type === selectedFieldType) &&
            (selectedObjectTypes.length === 0 ||
              selectedObjectTypes.includes(row.object_type ?? "")),
        )
        .map((row) => ({
          id: rowId(row.object_id),
          objectType: row.object_type ?? "",
          objectValue: rowId(row.object_value as any),
          fieldType: row.field_type ?? "",
          fieldValue: row.field_value ?? "",
          isReachable: row.object_type === SYSTEM_DEFAULT,
        })),
    [devObjects, selectedFieldType, selectedObjectTypes],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
    writePageParam(setSearchParams, 0);
  };

  const saveDraft = () => {
    if (!draft) return;
    // TODO: POST / PUT /aladdin_api/v1/data_collection/fetcher/objects
    // object_type must NOT be one of the four collection levels
    const id = draft.id ?? nextLocalId(objects);
    dispatch(
      upsertFetcherObjects([
        {
          id,
          lvl: LEVEL_ORDER.includes(draft.raw) ? draft.raw : "__bad__",
          raw: draft.raw.trim(),
          mid: draft.mid.trim(),
          k: draft.k.trim(),
          v: draft.v,
          ufr: "0",
          mod: nowStamp(),
        },
      ]),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${draft.k.trim()} written to objects.`,
        type: "success",
      }),
    );
    setDraft(null);
  };

  return (
    <>
      <DevObjectsFilterCard
        searchOptions={searchOptions}
        selectedFieldType={selectedFieldType}
        onSelectFieldType={(value) => writeSearchParam(setSearchParams, value)}
        objectTypeOptions={objectTypeOptions}
        selectedObjectTypes={selectedObjectTypes}
        onSelectedObjectTypesChange={setSelectedObjectTypes}
        onClearFilters={() => setSelectedObjectTypes([])}
        canCreate={canEdit}
        onCreate={() => setDraft(NEW_DRAFT)}
        onImportCsv={() => setIsImportOpen(true)}
      />

      {draft && (
        <DevObjectForm
          draft={draft}
          onChange={setDraft}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
        />
      )}

      {isCatalogueLoading ? (
        <DevObjectsSkeleton />
      ) : (
        <DevObjectsTable
          rows={rows}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(value) => writePageParam(setSearchParams, value)}
          onRowsPerPageChange={(value) =>
            writeRowsPerPageParam(setSearchParams, value)
          }
          canEdit={canEdit}
          onEdit={(id) => {
            const row = objects.find((object) => object.id === id);
            if (!row) return;
            setDraft({
              id: row.id,
              raw: row.raw,
              mid: row.mid,
              k: row.k,
              v: row.v,
            });
          }}
          onDelete={setDeleteId}
        />
      )}

      <ImportDevObjectsCsvModal
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      {deleteId && (
        <DeleteDevObjectModal
          open
          objectId={deleteId}
          onClose={() => setDeleteId(null)}
        />
      )}
    </>
  );
};

export default DevObjects;
